/**
 * What sharing a connector means, short first (item 80b, Segev 2026-11-02):
 * each explanation opens on a line or two, and "More" opens the rest in place.
 * Every sentence keeps the meaning verified against connector_library_service
 * in item 80 (availability, _reconcile / _detach, layers, the shared credential
 * by reference); only the wording is shorter.
 *
 * The views take `open` from their host, so `sharing_texts.test.mjs` renders
 * both states of the real markup.
 */

import { useId, useState } from 'react';
import { useLanguage } from '@hooks/useLanguage';

/** "More" / "Less": a real button, saying whether the region it names is open. */
export function MoreToggle({ open, onToggle, controls, testId }: {
  open: boolean; onToggle: () => void; controls: string; testId: string;
}) {
  const { t } = useLanguage();
  return (
    <button type="button" className="cxl-more" aria-expanded={open} aria-controls={controls}
            onClick={onToggle} data-testid={testId}>
      {t(open ? 'connectorLibrary.less' : 'connectorLibrary.more')}
    </button>
  );
}

/** The three statuses an agent's connector can have: its badge, and what it means. */
const STATUSES = [
  { key: 'shared', pill: 'cx-pill cx-pill--accent', label: 'connectorLibrary.status.sharedShort' },
  { key: 'customized', pill: 'cx-pill cxl-pill--customized', label: 'connectorLibrary.status.customized' },
  { key: 'own', pill: 'cx-pill', label: 'connectorLibrary.status.own' },
] as const;

/** What giving a connector to an organization means, behind "More" (the summary line says the rest). */
export const GIVING_POINTS = ['list', 'theirAdmins', 'below', 'takeAway', 'whoseKey'] as const;

/**
 * "How sharing works" (the list page's popover): two short sentences and the
 * three badges, one line each; what an organization above shares, what giving
 * does, and each badge in full behind "More".
 */
export function SharingHelpContent({ more, onMore }: { more: boolean; onMore: () => void }) {
  const { t } = useLanguage();
  const moreId = useId();
  return (
    <div className="cxl-help cxl-help--wide" data-testid="connector-sharing-help-content">
      <p className="cxl-help__p">{t('connectorLibrary.sharing.summary.list')}</p>
      <p className="cxl-help__p">{t('connectorLibrary.sharing.summary.giving')}</p>
      <p className="cxl-help__legend-title">{t('connectorLibrary.sharing.legend')}</p>
      <dl className="cxl-help__legend">
        {STATUSES.map(s => (
          <div key={s.key} className="cxl-help__legend-row">
            <dt><span className={s.pill}>{t(s.label)}</span></dt>
            <dd>{t(`connectorLibrary.sharing.badge.${s.key}`)}</dd>
          </div>
        ))}
      </dl>
      <div id={moreId} className="cxl-help__more" hidden={!more} data-testid="connector-sharing-help-detail">
        <p className="cxl-help__p">{t('connectorLibrary.sharing.shared')}</p>
        <p className="cxl-help__p" data-testid="connector-sharing-help-giving">{t('connectorLibrary.sharing.more.giving')}</p>
        {STATUSES.map(s => (
          <p key={s.key} className="cxl-help__p">
            <strong className="cxl-help__term">{t(s.label)}</strong>{' '}{t(`connectorLibrary.help.${s.key}`)}
          </p>
        ))}
      </div>
      <MoreToggle open={more} onToggle={onMore} controls={moreId} testId="connector-sharing-help-more" />
    </div>
  );
}

/** The Organizations tab's "What giving it to an organization means": a title
    and one line; "More" opens the five points in place. */
export function GivingMeansView({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const { t } = useLanguage();
  const titleId = useId();
  const listId = useId();
  return (
    <section className="cxl-giving" aria-labelledby={titleId} data-testid="library-orgs-giving">
      <h4 id={titleId} className="cxl-giving__title">{t('connectorLibrary.giving.title')}</h4>
      <p className="cxl-giving__summary">
        {t('connectorLibrary.giving.summary')}{' '}
        <MoreToggle open={open} onToggle={onToggle} controls={listId} testId="library-orgs-giving-more" />
      </p>
      <ul id={listId} className="cxl-giving__list" hidden={!open} data-testid="library-orgs-giving-list">
        {GIVING_POINTS.map(k => <li key={k}>{t(`connectorLibrary.giving.${k}`)}</li>)}
      </ul>
    </section>
  );
}

/** Collapsed until someone asks for more. */
export function GivingMeans() {
  const [open, setOpen] = useState(false);
  return <GivingMeansView open={open} onToggle={() => setOpen(o => !o)} />;
}
